import React, { useState, useEffect, useRef } from "react";
import icons from "./data/icons";
import { getYoutubeData } from "../components/api/youtubeApi";
import { Theme } from "emoji-picker-react";
import EmailLink from "./EmailLink";
// ─── THEME ────────────────────────────────────────────────────────────────────
const THEME = {
  bg: "#000000",
  card: "#0d0d0d",
  cardHover: "#0d1420",
  border: "rgba(255,255,255,0.08)",
  borderHover: "rgba(59,130,246,0.55)",
  text: "#ffffff",
  textMuted: "#9a9a9a",
  textFaint: "#6b6b6b",
  accent: "#3B82F6",
  accentOn: "#ffffff",
  accentGlow: "rgba(59,130,246,0.35)",
  red: "#ef4444",
};

const EMPTY_CHANNEL_META = {
  description: "",
  subscribers: "0",
  videos: "0",
  totalViews: "0",
  growthLabel: "—",
};

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const YTIcon = ({ size = 18, color = "#000" }) => (
  <svg width={size} height={size * 0.7} viewBox="0 0 28 20" fill="none">
    <path
      d="M27.4 3.14a3.5 3.5 0 0 0-2.46-2.48C22.7 0 14 0 14 0S5.3 0 3.06.66A3.5 3.5 0 0 0 .6 3.14C0 5.4 0 10 0 10s0 4.6.6 6.86a3.5 3.5 0 0 0 2.46 2.48C5.3 20 14 20 14 20s8.7 0 10.94-.66a3.5 3.5 0 0 0 2.46-2.48C28 14.6 28 10 28 10s0-4.6-.6-6.86z"
      fill={color}
    />
    <path d="M11.2 14.28V5.72L18.4 10l-7.2 4.28z" fill={THEME.bg === color ? "#000" : "#fff"} />
  </svg>
);

const GoogleGIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const UsersIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={THEME.accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const PlayIcon = ({ size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={THEME.accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const PlayIconFilled = ({ size = 13, color = "#fff" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const EyeIcon = ({ size = 17, color = THEME.accent }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const ExtIcon = ({ size = 12, color = THEME.accent }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const ClockIcon = ({ size = 12, color = THEME.textMuted }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15.5 14" />
  </svg>
);

const ShortsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#d81616">
    <path d="M17.77 10.32l-1.2-.5L18 9.06a3.74 3.74 0 0 0-3.5-6.56L6 6.94A3.75 3.75 0 0 0 4 10.25V18a3.75 3.75 0 0 0 3.75 3.75h8.5A3.75 3.75 0 0 0 20 18v-4a3.76 3.76 0 0 0-2.23-3.68zM10 15.5v-5l5 2.5-5 2.5z" />
  </svg>
);
function formatCount(value) {
  const num = Number(value);

  if (!Number.isFinite(num)) return "0";

  if (num >= 1000000) {
    return `${(num / 1000000)
      .toFixed(1)
      .replace(/\.0$/, "")}M`;
  }

  if (num >= 1000) {
    return `${(num / 1000)
      .toFixed(1)
      .replace(/\.0$/, "")}K`;
  }

  return `${num}`;
}
// ─── Formatting helpers ────────────────────────────────────────────────────────
function formatRelativeTime(timestamp) {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  const now = new Date();

  const diffSec = Math.floor((now - date) / 1000);

  if (diffSec < 10) return "Updated just now";
  if (diffSec < 60) return `Updated ${diffSec}s ago`;

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `Updated ${diffMin}m ago`;

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `Updated ${diffHour}h ago`;

  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `Updated ${diffDay}d ago`;

  // same month → June 20
  if (
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  ) {
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
    });
  }

  // last month / older → May 20, 2026
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatPublishedAgo(dateString) {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();

  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return "just now";

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;

  const diffDay = Math.floor(diffHour / 24);

  if (diffDay < 7) {
    return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
  }

  // same month → June 20
  if (
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  ) {
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
    });
  }

  // last month / older → May 20, 2026
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

// ─── Helper Components ────────────────────────────────────────────────────────
const StatCard = ({ icon, value, label }) => (
  <div
    style={{
      flex: "1 1 150px",
      background: "bg-gradient-to-br from-slate-900/60 to-slate-800/60 backdrop-blur-xl",
      border: `1px solid ${THEME.border}`,
      borderRadius: "14px",
      padding: "18px 20px",
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      minWidth: 0,
      transition: "border-color 0.25s ease",
    }}
  >
    {icon}
    <div>
      <div
        style={{
          fontSize: "clamp(20px,2.6vw,26px)",
          fontWeight: 800,
          color: THEME.text,
          letterSpacing: "-0.3px",
          lineHeight: 1,
          fontFamily: "'Inter', 'Segoe UI', sans-serif",
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: "10.5px",
          color: THEME.textMuted,
          marginTop: "6px",
          fontFamily: "'Inter', sans-serif",
          letterSpacing: "0.6px",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
    </div>
  </div>
);

const SectionLabel = ({ white, badge }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
    <h3
      style={{
        fontSize: "clamp(1.3rem,2.6vw,1.8rem)",
        fontWeight: 800,
        color: THEME.text,
        margin: 0,
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
      }}
    >
      {white}
    </h3>
    <span
      style={{
        background: THEME.accent,
        color: "#ffffff",
        fontWeight: 800,
        fontSize: "clamp(0.9rem,1.8vw,1.15rem)",
        padding: "2px 12px",
        borderRadius: "8px",
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
      }}
    >
      {badge}
    </span>
  </div>
);

const ComingSoonCard = ({ title, subtitle }) => (
  <div
    style={{
      width: "100%",
      minHeight: "200px",
      borderRadius: "14px",
      background: "bg-gradient-to-br from-slate-900/60 to-slate-800/60 backdrop-blur-xl",
      border: `1px solid ${THEME.border}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "24px",
    }}
  >
    <div>
      <div
        style={{
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          margin: "0 auto 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(59,130,246,0.12)",
          border: `1px solid ${THEME.accent}`,
          color: THEME.accent,
          fontSize: "20px",
          fontWeight: 700,
        }}
      >
        +
      </div>
      <h4 style={{ margin: "0 0 8px", color: THEME.text, fontSize: "15px", fontWeight: 700, fontFamily: "'Inter', sans-serif" }}>
        {title}
      </h4>
      <p style={{ margin: 0, color: THEME.textMuted, fontSize: "12.5px", lineHeight: 1.6, fontFamily: "'Inter', sans-serif" }}>
        {subtitle}
      </p>
    </div>
  </div>
);

const ErrorBanner = ({ message }) => (
  <div
    style={{
      marginBottom: "28px",
      padding: "13px 18px",
      borderRadius: "12px",
      border: `1px solid rgba(239,68,68,0.4)`,
      background: "rgba(239,68,68,0.08)",
      color: "#fca5a5",
      fontSize: "13px",
      fontFamily: "'Inter', sans-serif",
      textAlign: "center",
    }}
  >
    {message}
  </div>
);

const UpdatedBadge = ({ timestamp, refreshing }) => {
  if (!timestamp) return null;
  return <div />;
};

// ─── Thumbnail with automatic fallback chain ──────────────────────────────────
const Thumbnail = ({ video, alt, hovered }) => {
  const chain = useRef(buildThumbnailChain(video)).current;
  const [stepIndex, setStepIndex] = useState(0);

  const handleError = () => {
    setStepIndex((i) => (i + 1 < chain.length ? i + 1 : i));
  };

  return (
    <img
      src={chain[stepIndex]}
      alt={alt}
      loading="lazy"
      onError={handleError}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
        transition: "transform 0.5s",
        transform: hovered ? "scale(1.06)" : "scale(1)",
      }}
    />
  );
};

function buildThumbnailChain(video) {
  const chain = [];
  if (video?.thumbnail) chain.push(video.thumbnail);
  if (video?.id) {
    chain.push(`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`);
    chain.push(`https://i.ytimg.com/vi/${video.id}/mqdefault.jpg`);
  }
  chain.push(
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(
      "<svg xmlns='http://www.w3.org/2000/svg' width='320' height='180'>" +
      "<rect width='100%' height='100%' fill='#111111'/>" +
      "<text x='50%' y='50%' fill='#555' font-family='sans-serif' font-size='14' " +
      "text-anchor='middle' dominant-baseline='middle'>No thumbnail</text></svg>"
    )
  );
  return [...new Set(chain)];
}

// ─── Unified Media Card (used for BOTH videos and shorts) ─────────────────────
const MediaCard = ({ video, index, isVisible, isShort }) => {
  const [hovered, setHovered] = useState(false);
  const url = isShort
    ? `https://www.youtube.com/shorts/${video.id}`
    : `https://www.youtube.com/watch?v=${video.id}`;

  return (
    <div
      onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="link"
      tabIndex={0}
      aria-label={`Watch: ${video.title}`}
      onKeyDown={(e) => e.key === "Enter" && window.open(url, "_blank")}
      style={{
        borderRadius: "14px",
        overflow: "hidden",
        cursor: "pointer",
        background: THEME.card,
        border: `1px solid ${hovered ? THEME.borderHover : THEME.border}`,
        transition: "all 0.3s cubic-bezier(.4,0,.2,1)",
        transform: isVisible ? (hovered ? "translateY(-3px)" : "translateY(0)") : "translateY(24px)",
        opacity: isVisible ? 1 : 0,
        transitionDelay: isVisible ? `${(index % 3) * 90}ms` : "0ms",
        boxShadow: hovered ? `0 12px 28px -8px ${THEME.accentGlow}` : "0 4px 14px rgba(0,0,0,0.4)",
      }}
    >
      <div style={{ position: "relative", width: "100%", aspectRatio: "16/9", background: "#111", overflow: "hidden" }}>
        <Thumbnail video={video} alt={video.title} hovered={hovered} />

        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.05) 45%, rgba(0,0,0,0) 100%)",
          }}
        />

        <div
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            width: 28,
            height: 28,
            borderRadius: "6px",
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isShort ? <ShortsIcon /> : <GoogleGIcon size={16} />}
        </div>

        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            opacity: hovered ? 1 : 0,
            transition: "opacity 0.25s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              background: THEME.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 0 18px ${THEME.accentGlow}`,
            }}
          >
            <PlayIconFilled size={15} />
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            left: 12,
            right: 12,
            bottom: 10,
            fontSize: "13px",
            fontWeight: 700,
            color: "#fff",
            lineHeight: 1.4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textShadow: "0 2px 6px rgba(0,0,0,0.8)",
            fontFamily: "'Inter', 'Segoe UI', sans-serif",
          }}
        >
          {video.title}
        </div>
      </div>

      <div
        style={{
          padding: "10px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", color: THEME.textMuted, fontSize: "11px", fontFamily: "'Inter', sans-serif" }}>
            <EyeIcon size={13} />
            {formatCount(video.viewCount)}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", color: THEME.textMuted, fontSize: "11px", fontFamily: "'Inter', sans-serif" }}>
            <ClockIcon size={12} />
            {formatPublishedAgo(video.publishedAt)}
          </span>
        </div>
        <span style={{ color: hovered ? THEME.accent : THEME.textFaint, transition: "color 0.25s", flexShrink: 0 }}>
          <ExtIcon size={12} color="currentColor" />
        </span>
      </div>
    </div>
  );
};

// ─── Main Section ─────────────────────────────────────────────────────────────
const YouTubeSection = () => {
  const [channelMeta, setChannelMeta] = useState(EMPTY_CHANNEL_META);
  const [videosData, setVideosData] = useState([]);
  const [shortsData, setShortsData] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [youtubeConfig, setYoutubeConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(true);
  const [, forceTick] = useState(0);

  const [visibleVideos, setVisibleVideos] = useState(new Set());
  const [visibleShorts, setVisibleShorts] = useState(new Set());

  const videoRefs = useRef([]);
  const shortRefs = useRef([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setRefreshing(true);
        setError(null);

        const response = await getYoutubeData();

        if (cancelled) return;

        const data = response?.data?.data;

        if (!data) {
          throw new Error("Invalid YouTube response");
        }

        // Channel
        setChannelMeta(
          data.channel || EMPTY_CHANNEL_META
        );

        // Videos
        setVideosData(
          data.videos || []
        );

        // Shorts
        setShortsData(
          data.shorts || []
        );

        // Database configuration
        setYoutubeConfig(
          data.config || null
        );

        // Dynamic social links
        setSocialLinks(
          data.config?.socialLinks || []
        );

        setLastUpdated(Date.now());

        const hasAnyData =
          (data.videos?.length || 0) > 0 ||
          (data.shorts?.length || 0) > 0;

        if (!hasAnyData) {
          setError(
            "No videos found for this channel yet."
          );
        } else {
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;

        console.error(
          "YouTube section failed to load:",
          err
        );

        setError(
          "Unable to load YouTube content right now. Please try again later."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => forceTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const observe = (refs, setter) => {
    const clean = refs.current.filter(Boolean);
    const obs = clean.map((ref, i) => {
      const o = new IntersectionObserver(
        (entries) =>
          entries.forEach((e) => {
            if (e.isIntersecting) setter((p) => new Set([...p, i]));
          }),
        { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
      );
      o.observe(ref);
      return o;
    });
    return () => obs.forEach((o) => o.disconnect());
  };

  useEffect(() => observe(videoRefs, setVisibleVideos), [videosData]);
  useEffect(() => observe(shortRefs, setVisibleShorts), [shortsData]);

  const limitedVideos = videosData.slice(0, 8);
  const limitedShorts = shortsData.slice(0, 8);

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill,minmax(min(100%,240px),1fr))",
    gap: "18px",
    marginBottom: "56px",
  };
  if (loading) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-[#0C121D]">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-indigo-500/30 blur-xl" />

          <div
            className="
            relative
            h-14 w-14
            rounded-full
            border-[5px]
            border-slate-700
            border-t-indigo-500
            animate-spin
          "
          />
        </div>
      </section>
    );
  }
  // Hide the entire YouTube section when it is disabled
if (youtubeConfig?.isActive !== true) {
  return null;
}
  return (
    <section
      id="youtube"
      className="relative bg-[#0C121D] pt-16 sm:pt-20 pb-32 sm:pb-40 px-3 sm:px-6 lg:px-8 overflow-hidden isolate"
    >
      <style>{`
        @keyframes yt-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.5); }
        }
        .yt-channel-name {
          white-space: normal;
          overflow: visible;
          text-overflow: unset;
        }
        @media (min-width: 640px) {
          .yt-channel-name {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 420px;
          }
        }
      `}</style>

      <div
        style={{
          position: "relative",
          zIndex: 1,
          maxWidth: "1280px",
          margin: "0 auto",
        }}
        className="px-0 sm:px-2 lg:px-8"
      >
        <div style={{ textAlign: "center", marginBottom: "44px" }}>
          <h2
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              flexWrap: "wrap",
              fontSize: "clamp(24px,3.6vw,38px)",
              fontWeight: 800,
              margin: "0 0 12px",
              fontFamily: "'Inter', 'Segoe UI', sans-serif",
            }}
          >
            <span style={{ color: THEME.text }}>
              {youtubeConfig?.content?.sectionTitle || "YouTube"}
            </span>

            <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              {youtubeConfig?.content?.sectionSubtitle || "Channel"}
            </span>
          </h2>

          {youtubeConfig?.banner && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "24px",
              }}
            >
              <img
                src={youtubeConfig.banner}
                alt="YouTube Channel Banner"
                style={{
                  width: "100%",
                  maxWidth: "900px",
                  borderRadius: "16px",
                  objectFit: "cover",
                  border: `1px solid ${THEME.border}`,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                  display: "block",
                }}
              />
            </div>
          )}

          <div className="flex flex-col lg:flex-row items-center justify-center gap-4 mb-4">
            <div className="flex flex-col sm:flex-row items-center sm:items-center justify-center gap-2 sm:gap-3 text-center">
              {channelMeta.avatar && (
                <img
                  src={channelMeta.avatar}
                  alt={channelMeta.title || channelMeta.name}
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: `3px solid ${THEME.accent}`,
                    boxShadow: `0 0 16px ${THEME.accentGlow}`,
                    flexShrink: 0,
                  }}
                />
              )}

              <h3
                className="yt-channel-name"
                style={{
                  margin: 0,
                  color: THEME.text,
                  fontSize: "clamp(20px,4vw,32px)",
                  fontWeight: 800,
                  fontFamily: "'Inter', 'Segoe UI', sans-serif",
                  letterSpacing: "-0.5px",
                }}
              >
                {channelMeta.title || channelMeta.name}
              </h3>
            </div>

            <div className="flex items-center justify-center flex-wrap gap-3 sm:gap-4">
              {socialLinks.map((item) => {
                const Icon = icons[item.icon];

                if (!Icon) return null;
                const isEmail = item.name?.toLowerCase() === "email";
                // Email → use EmailLink
                if (isEmail) {
                  return (
                    <EmailLink
                      key={item.id || item.name}
                      email={item.url}
                      icon={
                        <Icon
                          className="transition-transform duration-300 group-hover:scale-125"
                          size={
                            window.innerWidth < 640
                              ? 17
                              : window.innerWidth < 1024
                                ? 19
                                : 16
                          }
                          color={item.color}
                        />
                      }
                      className="
        group
        w-10 h-10
        sm:w-11 sm:h-11
        md:w-12 md:h-12
        lg:w-9 lg:h-9
        flex
        items-center
        justify-center
        rounded-full
        bg-transparent
        backdrop-blur-md
        border
        border-white/20
        transition-all
        duration-300
        ease-out
        hover:-translate-y-1
        hover:scale-110
        active:scale-95
      "
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.borderColor = item.color;
                        e.currentTarget.style.boxShadow =
                          `0 0 18px ${item.color}`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.borderColor =
                          "rgba(255,255,255,0.2)";
                        e.currentTarget.style.boxShadow =
                          "0 0 0px transparent";
                      }}
                    >
                      <span
                        className="
    absolute
    bottom-full
    left-1/2
    -translate-x-1/2
    mb-2

    w-fit
    max-w-[220px]

    px-3
    py-1.5

    rounded-lg
    text-white

    text-xs
    font-semibold
    whitespace-nowrap
    leading-none

    shadow-lg

    opacity-0
    translate-y-1
    group-hover:opacity-100
    group-hover:translate-y-0

    transition-all
    duration-200
    ease-out

    pointer-events-none
    z-50
  "
                        style={{
                          backgroundColor: item.color || "#94a3b8",
                          color: "#111010",
                        }}
                      >
                        {item.text || item.title || item.name}

                        <span
                          className="
      absolute
      top-full
      left-1/2
      -translate-x-1/2

      w-0
      h-0

      border-l-[6px]
      border-r-[6px]
      border-t-[6px]

      border-l-transparent
      border-r-transparent
    "
                          style={{
                            borderTopColor: item.color || "#94a3b8",
                          }}
                        />
                      </span>
                    </EmailLink>
                  );
                }

                // Other social links → normal <a>
                return (
                  <a
                    key={item.id || item.name}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
          group
          w-10 h-10
          sm:w-11 sm:h-11
          md:w-12 md:h-12
          lg:w-9 lg:h-9
          flex
          items-center
          justify-center
          rounded-full
          bg-transparent
          backdrop-blur-md
          border
          border-white/20
          transition-all
          duration-300
          ease-out
          hover:-translate-y-1
          hover:scale-110
          active:scale-95
        "
                    style={{
                      boxShadow: "0 0 0px transparent",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.borderColor = item.color;
                      e.currentTarget.style.boxShadow =
                        `0 0 18px ${item.color}`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.borderColor =
                        "rgba(255,255,255,0.2)";
                      e.currentTarget.style.boxShadow =
                        "0 0 0px transparent";
                    }}
                  >
                    <Icon
                      className="
            transition-transform
            duration-300
            group-hover:scale-125
          "
                      size={
                        window.innerWidth < 640
                          ? 17
                          : window.innerWidth < 1024
                            ? 19
                            : 16
                      }
                      color={item.color}
                    />
                    <span
                      className="
    absolute
    bottom-full
    left-1/2
    -translate-x-1/2
    mb-2

    w-fit
    max-w-[220px]

    px-3
    py-1.5

    rounded-lg
    text-white

    text-xs
    font-semibold
    whitespace-nowrap
    leading-none

    shadow-lg

    opacity-0
    translate-y-1
    group-hover:opacity-100
    group-hover:translate-y-0

    transition-all
    duration-200
    ease-out

    pointer-events-none
    z-50
  "
                      style={{
                        backgroundColor: item.color || "#94a3b8",
                      }}
                    >
                      {item.text || item.title || item.name}

                      <span
                        className="
      absolute
      top-full
      left-1/2
      -translate-x-1/2

      w-0
      h-0

      border-l-[6px]
      border-r-[6px]
      border-t-[6px]

      border-l-transparent
      border-r-transparent
    "
                        style={{
                          borderTopColor: item.color || "#94a3b8",
                        }}
                      />
                    </span>
                  </a>
                );
              })}
            </div>
          </div>
          <p
            style={{
              color: "white",
              fontSize: "15px",
              lineHeight: 1.7,
              fontWeight: 400,
              fontFamily: "'Inter', 'Segoe UI', sans-serif",
              maxWidth: "760px",
              margin: "0 auto 24px",
              textAlign: "start",
            }}
          >
            {channelMeta.description}
          </p>

          <UpdatedBadge timestamp={lastUpdated} refreshing={refreshing && !lastUpdated} />
        </div>

        {error && <ErrorBanner message={error} />}

        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "56px" }}>
          <StatCard icon={<UsersIcon />} value={channelMeta.subscribers} label="Subscribers" />
          <StatCard icon={<PlayIcon size={16} />} value={channelMeta.videos} label="Videos" />
          <StatCard icon={<EyeIcon size={16} />} value={channelMeta.totalViews} label="Total Views" />
        </div>

        {limitedVideos.length > 0 && (
          <>
            <SectionLabel white="Latest" badge="Videos" />
            <div style={gridStyle}>
              {limitedVideos.map((v, i) => (
                <div key={v.id} ref={(el) => (videoRefs.current[i] = el)}>
                  <MediaCard video={v} index={i} isVisible={visibleVideos.has(i)} isShort={false} />
                </div>
              ))}
            </div>
          </>
        )}

        {limitedShorts.length > 0 && (
          <>
            <SectionLabel white="Latest" badge="Shorts" />
            <div style={gridStyle}>
              {limitedShorts.map((v, i) => (
                <div key={v.id} ref={(el) => (shortRefs.current[i] = el)}>
                  <MediaCard video={v} index={i} isVisible={visibleShorts.has(i)} isShort={true} />
                </div>
              ))}
            </div>
          </>
        )}

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
          <a
            href={`https://www.youtube.com/channel/${youtubeConfig?.channelId}?sub_confirmation=1`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "9px",
              padding: "13px 28px",
              borderRadius: "999px",
              background: THEME.border,
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: 800,
              textDecoration: "none",
              fontFamily: "'Inter', sans-serif",
              letterSpacing: "0.2px",
              transition: "all 0.25s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            <icons.FaYoutube size={25} color="red" /> Subscribe on YouTube <ExtIcon size={12} color="#ffffff" />
          </a>
          <p style={{ margin: 0, color: THEME.textFaint, fontSize: "12px", fontFamily: "'Inter', sans-serif" }}>
            Join for the latest content
          </p>
        </div>
      </div>
    </section>
  );
};

export default YouTubeSection;